import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { enquiryMailto } from "@/lib/enquiry";
import { ENQUIRY_EMAIL } from "@/lib/brand";

export const Route = createFileRoute("/legal/terms-of-use")({
  component: TermsOfUsePage,
});

function TermsOfUsePage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>Terms of Use | Larasoft</title>
        <meta
          name="description"
          content="Larasoft Terms of Use - the legally binding agreement governing your use of Larasoft's cloud reporting and data integration services."
        />
        <link
          rel="canonical"
          href="https://larasoft.global/legal/terms-of-use"
        />
      </Helmet>
      <Section className="py-16">
        <div className="mx-auto max-w-4xl">
          <h1 className="mb-8 text-4xl font-bold">Terms of Use</h1>

          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-700">
              Last Updated: 01 September 2025
            </p>

            <p>
              These Terms of Use (&ldquo;Terms&rdquo;) are a legally binding
              agreement between you (&ldquo;you&rdquo;, &ldquo;your&rdquo;,
              &ldquo;Customer&rdquo;, &ldquo;Subscriber&rdquo;) and Larasoft
              (Pty) Ltd (&ldquo;Larasoft&rdquo;, &ldquo;we&rdquo;,
              &ldquo;us&rdquo;, &ldquo;our&rdquo;), a company registered in
              South Africa, registration number 2019/191486/07, with its
              principal place of business Cape Town, Western Cape, South Africa.
            </p>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">1. Definitions</h2>
              <ul className="mt-4 space-y-2">
                <li>
                  <strong>Subscription:</strong> the access rights to the
                  Services granted under a plan selected by you, subject to
                  payment of fees and compliance with these Terms.
                </li>
                <li>
                  <strong>Services:</strong> all software, platform, tools,
                  support, documentation, and use thereof made available by
                  Larasoft to you under the Subscription, now or in the future.
                </li>
                <li>
                  <strong>Subscriber:</strong> the person or legal entity who has
                  contracted with Larasoft for a Subscription.
                </li>
                <li>
                  <strong>User(s):</strong> individuals invited by the Subscriber
                  to use the Services under the Subscription.
                </li>
                <li>
                  <strong>Effective Date:</strong> the date on which you first
                  accept these Terms or first subscribe to the Services.
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                2. Subscription, Billing &amp; Payment
              </h2>
              <p>
                Subscriptions commence on the Effective Date and are for
                calendar-month periods unless otherwise agreed.
              </p>
              <p>
                You agree to pay all subscription fees in advance, according to
                the pricing plan selected. All fees are in South African Rand
                (ZAR) for customers based in South Africa, and in United States
                Dollars (USD) for all other customers, unless otherwise stated.
              </p>
              <p>
                We may require you to provide a valid method of payment. Late or
                failed payments may result in suspension or termination of your
                access.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                3. Notice Period &amp; Termination
              </h2>
              <p>
                Either you or we may terminate the Subscription by giving one
                calendar month&apos;s written notice.
              </p>
              <p>
                The notice period starts from the date we receive the written
                notice.
              </p>
              <p>
                You remain liable for all subscription fees up to and including
                the end of the notice period.
              </p>
              <p>
                We may terminate or suspend your access immediately (without
                notice) if you breach any material term of these Terms and fail
                to remedy it within 14 days after receiving written notice from
                us.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                4. Automatic Renewal
              </h2>
              <p>
                Unless either party has given notice under section 3, the
                Subscription will automatically renew at the end of each
                subscription period for another calendar month, under the same
                plan and terms, subject to any changes to pricing which we will
                notify you about in advance.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                5. Use of the Services
              </h2>
              <p>
                You agree to use the Services only for your own internal
                business purposes and in compliance with all applicable laws.
                Users invited by you must also comply with these Terms. You are
                responsible for their usage.
              </p>
              <p>You must not:</p>
              <ul className="mt-4 space-y-2">
                <li>use the Services in any unlawful way;</li>
                <li>infringe intellectual property rights;</li>
                <li>
                  transmit malicious code, abuse, or disrupt the Services;
                </li>
                <li>
                  reverse engineer, decompile or otherwise attempt to derive
                  source code or underlying logic of the Services, except where
                  permitted by law.
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                6. Data, Privacy &amp; Security
              </h2>
              <p>
                You retain ownership of all data you upload or input into the
                Services (&ldquo;Customer Data&rdquo;). We do not own your data.
              </p>
              <p>
                You grant Larasoft a licence to use, copy, store, process,
                transmit and back up Customer Data as required to provide the
                Services, improve them, and prevent misuse.
              </p>
              <p>
                Larasoft will comply with all applicable data protection laws in
                South Africa (such as the POPIA) in handling Customer Data.
              </p>
              <p>
                We use reasonable security measures to protect your data, but
                cannot guarantee absolute security.
              </p>
              <p>
                Our separate{" "}
                <a
                  href="/legal/privacy"
                  className="text-blue-600 underline"
                >
                  Privacy Policy
                </a>{" "}
                explains how we collect, use and protect your personal
                information.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                7. Maintenance, Downtime &amp; Data Loss
              </h2>
              <p>
                We aim to ensure high availability, but you acknowledge that
                planned maintenance, emergencies, or external third-party
                failures may lead to service downtime.
              </p>
              <p>
                Larasoft will endeavour to notify you of planned maintenance in
                advance.
              </p>
              <p>
                We are not responsible for loss of data which is due to your
                failure to maintain backups.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                8. Intellectual Property
              </h2>
              <p>
                Larasoft owns all intellectual property in the Services
                (software, designs, trademarks, etc.), except for your Customer
                Data and any third-party materials.
              </p>
              <p>
                You receive a non-exclusive, non-transferable, revocable right
                to use the Services in accordance with these Terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                9. Liability &amp; Indemnity
              </h2>
              <p>
                To the maximum extent permitted by law, Larasoft&apos;s
                liability to you is limited to the total subscription fees you
                have paid in the 12 months preceding the event giving rise to
                liability.
              </p>
              <p>
                We are not liable for indirect, incidental, consequential, or
                special damages (loss of profits, goodwill, data, etc.).
              </p>
              <p>
                You indemnify Larasoft against any losses, costs or damages
                arising from: your misuse of the Services; breach of these
                Terms; violation of applicable laws; or claims by third parties
                due to your use of the Services.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                10. Changes to Terms &amp; Pricing
              </h2>
              <p>
                We may change these Terms, pricing, or features of the Services
                from time to time.
              </p>
              <p>
                If a change is material, we will give you at least 60
                days&apos; notice via email or through our platform before the
                change takes effect.
              </p>
              <p>
                If you do not agree with a material change, you may terminate
                the Subscription under section 3.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                11. Dispute Resolution &amp; Governing Law
              </h2>
              <p>
                These Terms are governed by the laws of the Republic of South
                Africa.
              </p>
              <p>
                Any dispute arising under or in connection with these Terms
                shall be subject to the non-exclusive jurisdiction of the courts
                in Cape Town, Western Cape, South Africa.
              </p>
              <p>
                Before commencing formal legal proceedings, we both agree to
                attempt to resolve disputes in good faith by negotiation.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">12. Miscellaneous</h2>
              <ul className="mt-4 space-y-2">
                <li>
                  <strong>Notices:</strong> Notices must be given in writing by
                  email to the contact details either party has provided. For
                  Larasoft, notices must be sent to{" "}
                  <a
                    href="mailto:legal@larasoft.global"
                    className="text-blue-600 underline"
                  >
                    legal@larasoft.global
                  </a>
                  .
                </li>
                <li>
                  <strong>Assignment:</strong> You may not assign or transfer
                  your rights or obligations under these Terms without our prior
                  written consent. We may assign ours.
                </li>
                <li>
                  <strong>Severability:</strong> If any provision is found
                  invalid, illegal or unenforceable, the rest of the Terms will
                  remain in effect.
                </li>
                <li>
                  <strong>Entire Agreement:</strong> These Terms (plus any
                  accepted proposal or order form, privacy policy, data
                  processing agreement, or other written agreement between us)
                  represent the whole agreement between you and Larasoft relating
                  to the Services.
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">Contact Us</h2>
              <p>
                For general queries, please contact us at{" "}
                <a
                  href={enquiryMailto("/legal/terms-of-use")}
                  className="text-blue-600 underline"
                >
                  {ENQUIRY_EMAIL}
                </a>
                .
              </p>
              <p className="mt-4">
                For legal notices, please contact{" "}
                <a
                  href="mailto:legal@larasoft.global"
                  className="text-blue-600 underline"
                >
                  legal@larasoft.global
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
